import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {DashboardMachine} from '@models/dashboard/dashboard-machine';
import {DashboardMapProperties} from '@models/dashboard/dashboard-map-properties';
import {GoogleMap, MapInfoWindow, MapMarker} from '@angular/google-maps';
import {MarkerClustererService} from '@services/dashboard/marker-clusterer/marker-clusterer.service';
import {GeocodingService} from '@services/administration/machine/geocoding/geocoding.service';
import {DashboardService} from '@services/dashboard/dashboard.service';
import {EnvironmentConfig} from '@environments/environment';
import {
  DEFAULT_MAX_ZOOM_LEVEL,
  GOOGLE_MAPS_DIRECTION_URL,
  MAX_MACHINES_NUMBERS_FOR_POLLING
} from '@constants/google-maps/google-maps.constant';
import {RealtimePollingService} from '@services/realtime-polling/realtime-polling.service';
import {RealtimePollingRequestParameters} from '@services/realtime-polling/realtime-polling-request-parameters';
import {debounceTime, filter, startWith, switchMap, takeUntil,} from 'rxjs/operators';
import {MachineDetailsService} from '@services/dashboard/machine-details.service';
import {forkJoin, interval, Observable, of, Subject, Subscription} from 'rxjs';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {MenuItem} from '@models/sidebar/menu-item';
import {TRAFFIC_LIGHT_STATUS_CLASS} from '@constants/machine/traffic-light/traffic-light-status';
import {User} from '@models/user/user';
import {SettingsService} from '@services/administration/settings/settings.service';
import {Role} from '@models/user/role';

@Component({
    selector: 'scitis-machine-map',
    templateUrl: './machine-map.component.html',
    styleUrls: ['./machine-map.component.scss'],
    standalone: false
})
export class MachineMapComponent implements OnInit, OnDestroy,OnChanges {

  @Input()
  public groupId: string = '';

  @Input()
  public currentLocation: string = '';

  @Input()
  public user: User = new User();

  @ViewChild('googleMap')
  public map: GoogleMap | undefined;

  public mapSettings!: DashboardMapProperties;
  public googleMapsLoaded: boolean = false;
  public mapSettingsInitialized: boolean = false;
  public currentCluster: google.maps.Marker[] = [];
  public currentMarker: google.maps.Marker | undefined;
  public clusterAlgorithm: any;
  public clusterRenderer: any;

  private machines: DashboardMachine[] = [];
  private MACHINE_DATA_URL_SUFFIX: string = '/api/web/tenant/:tenant/machine/:machine/tile?refresh=:refresh&sampling=:sampling';
  private SAMPLING_RATE: number = EnvironmentConfig.PortalSettings.tileSamplingInterval;
  private REFRESH_INTERVAL: number = 300;
  private destroy: Subject<boolean> = new Subject<boolean>();
  private currentPosition: GeolocationPosition | undefined;
  private visibleMachines: DashboardMachine[] = [];
  private pollingEvent: EventEmitter<google.maps.LatLng> = new EventEmitter<google.maps.LatLng>();
  private geocoder: google.maps.Geocoder | undefined;

  constructor(
    private markerClustererService: MarkerClustererService,
    private geocodingService: GeocodingService,
    private pollingService: RealtimePollingService,
    private machineDetailsService: MachineDetailsService,
    private dashboardService: DashboardService,
    private menuNotificationService: MenuNotificationService,
    private settingsService: SettingsService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  public get currentPositionAvailable(): boolean {
    return !!this.currentPosition?.coords;
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes?.currentLocation) {
      this.ngOnDestroy();
      this.initSubscriptions();
    }
  }

  ngOnDestroy() {
    this.destroy.next(true);
    this.destroy.complete();
  }

  public openInfoWindow(mapMarker: MapMarker, infoWindow: MapInfoWindow): void {
    this.currentMarker = mapMarker.marker;
    infoWindow.open(mapMarker);
  }

  public onClusterClick(e, window: MapInfoWindow): void {
    if (this.map?.googleMap?.getZoom() === DEFAULT_MAX_ZOOM_LEVEL) {
      this.currentCluster = e.getMarkers();
      const firstMarker: google.maps.Marker = e.getMarkers()[0];
      window.position = firstMarker.getPosition() as google.maps.LatLng;
      window.open();
    }
  }

  public defineStatusIcon(trafficLight: TRAFFIC_LIGHT_STATUS_CLASS): string {
    return this.markerClustererService.defineStatusIcon(trafficLight);
  }

  public redirectToGoogleMaps(position): void {
    let url: string;
    const {lat, lng} = position;
    if (this.currentPosition) {
      const {coords} = this.currentPosition;
      url = GOOGLE_MAPS_DIRECTION_URL + `${coords.latitude},${coords.longitude}/${lat()},${lng()}`;
    } else {
      url = GOOGLE_MAPS_DIRECTION_URL + `${lat()},${lng()}`;
    }
    window.open(url, '_blank');
  }

  public redirectToFirstMenuEntry(marker): void {
    const id = this.groupId + '_' + marker.machineId;
    this.dashboardService.getItemMenuItemAndParentMenuItem(id, true)
      .pipe(takeUntil(this.destroy)).subscribe((machineMenuItem: MenuItem) => {
      this.menuNotificationService.broadcastClickOnDashboard(machineMenuItem);
    });
  }

  public startPollingForVisiblePins(): void {
    this.visibleMachines = this.geocodingService.getVisibleMachines(this.map?.getBounds() as google.maps.LatLngBounds, this.machines, this.mapSettings.markers);
    const center = this.map?.getCenter();
    if (center) {
      this.pollingEvent.next(center);
    }
  }

  private setMarkers(): void {
    if (!this.mapSettingsInitialized) {
      this.mapSettings = new DashboardMapProperties();
      this.mapSettingsInitialized = true;
    }

    this.markerClustererService.setMarkers(this.mapSettings, this.machines);
    this.clusterAlgorithm = this.markerClustererService.getClusterAlgorithm();
    this.clusterRenderer = this.markerClustererService.getClusterRenderer();
  }

  private setMarkersOnMap(): Subscription {
    return this.geocodingService.googleMapsLoaded
      .pipe(
        filter(res => res),
        takeUntil(this.destroy),
        switchMap((data) => {
          this.googleMapsLoaded = data;
          this.geocoder = new google.maps.Geocoder();
          if (data && this.machines?.length) {
            this.initMarkers();
          }
          return interval(EnvironmentConfig.PortalSettings.machineMapIntervalInMilliseconds);
        }))
      .pipe(
        startWith(0),
        switchMap(() => this.getMachines())
      ).subscribe({
        next: (response) => {
          this.machines = [];
          this.machines = this.filterMachinesList(Array.from(response));
          if (this.googleMapsLoaded) {
            this.initMarkers();
          } else {
            startWith(0);
          }
        },
        error: () => {
          this.machines = [];
          this.setMarkers();
        }
      });
  }

  private initMarkers(): void {
    this.setMarkers();
    if (this.map?.getZoom()) {
      const center = this.map?.getCenter();
      if (center) {
        this.pollingEvent.next(center);
      }
    }
  }

  private getMachines(): Observable<Set<DashboardMachine>> {
    const userRole = new Role(this.user);
    let machineryGroupId: string = String(this.groupId);
    if (!machineryGroupId || machineryGroupId.isNullOrWhitespace()) {
      machineryGroupId = '-1';
    }
    const whiteListedMachineryGroups = userRole.isSuperAdminRole ? null : this.user.getWhiteListedMachineryGroups(true);

    const organizationId = userRole.isSuperAdminRole ? -1 : this.user.parentId;

    return this.dashboardService.getDashboardTileMachinesFromElastic(machineryGroupId, organizationId, this.currentLocation, 1, 9999, whiteListedMachineryGroups);

  }

  private filterMachinesList(machines: DashboardMachine[]): DashboardMachine[] {
    let similarLocationArr: DashboardMachine[] = [];
    const difLocationArr: DashboardMachine[] = [];

    machines.forEach((item, machinesArrayIdx, self) => {

      if (item.location && item.locationDetails && item.locationDetails.latitude && item.locationDetails.longitude) {
        const machine = self.find(({datasetLocation, location, locationDetails}, selfArrayIdx) => {
          if (machinesArrayIdx === selfArrayIdx) {
            return false;
          }

          return item.datasetLocation === datasetLocation &&
            item.location === location &&
            item.locationDetails?.latitude === locationDetails?.latitude &&
            item.locationDetails?.longitude === locationDetails?.longitude;
        });

        !!machine ? similarLocationArr.push(item) : difLocationArr.push(item);
      }
    });

    similarLocationArr = this.geocodingService.updateMachinesPosition(similarLocationArr);

    return similarLocationArr.concat(difLocationArr);
  }

  private setTrafficLightClass(results): void {
    results.forEach((item, index) => {
      const gatewaysStatus = this.machineDetailsService
        .determineAggregatesGatewaysStatus(item[0]?.cloudplugs, results[0].missingConfiguration);
      const trafficLightClass = this.machineDetailsService
        .determineTileTrafficLightClass(item[0], gatewaysStatus);

      if (this.visibleMachines[index].trafficLightClass !== trafficLightClass) {
        this.visibleMachines[index].trafficLightClass = trafficLightClass;
      }
    });
  }

  private getPollingRequest(machine: DashboardMachine): Observable<any> {
    const pollingRequestParameters: RealtimePollingRequestParameters = new RealtimePollingRequestParameters({
      machineId: machine.id,
      organizationId: machine.parentId,
      refresh: this.REFRESH_INTERVAL,
      sampling: this.SAMPLING_RATE
    });

    return this.pollingService.getDataFromBackend(this.MACHINE_DATA_URL_SUFFIX, pollingRequestParameters);
  }

  private initSubscriptions() {

    this.settingsService.isLiveDataStreamingEnabled().subscribe({
      next: value => {
        if (value) {
          this.startPollingEvent();
        }
      }
    });

    this.geocodingService.getCurrentPosition()
      .pipe(takeUntil(this.destroy))
      .subscribe(pos => {
        this.currentPosition = pos;
      });

    this.setMarkersOnMap();
  }

  private startPollingEvent() {
    this.pollingEvent.pipe(
        filter(() => !!this.map && this.visibleMachines?.length <= MAX_MACHINES_NUMBERS_FOR_POLLING),
        takeUntil(this.destroy),
        debounceTime(500),
        switchMap(() => {
          this.visibleMachines = this.visibleMachines.filter((machine) => !machine.trafficLightClass);
          return this.visibleMachines?.length && this.visibleMachines?.length <= MAX_MACHINES_NUMBERS_FOR_POLLING
              ? forkJoin(this.visibleMachines.map(item => this.getPollingRequest(item)))
              : of([]);
        })).subscribe(results => {
      if (results?.length) {
        this.setTrafficLightClass(results);
        this.setMarkers();
        this.changeDetectorRef.detectChanges();
      }
    });
  }
}
